import { CopyIdButton } from '@/components/shared';
import { BackofficeTagChips } from './BackofficeTagChips';
import {
  formatCardLabel,
  formatDateTime,
  formatMinorAmount,
  formatSalesStatusLabel,
} from '../format';
import type { BackofficeOrganizationSalesHistoryEntry } from '../types';

const STATUS_CLASSES: Record<BackofficeOrganizationSalesHistoryEntry['status'], string> = {
  completed: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-amber-100 text-amber-800',
  refunded: 'bg-blue-100 text-blue-800',
  partially_refunded: 'bg-sky-100 text-sky-800',
  canceled: 'bg-neutral-200 text-neutral-700',
};

export interface OrganizationSalesTableProps {
  organizationName: string;
  entries: BackofficeOrganizationSalesHistoryEntry[];
}

function StatusBadge({ status }: { status: BackofficeOrganizationSalesHistoryEntry['status'] }) {
  return (
    <span
      className={[
        'inline-flex rounded-full px-2.5 py-1 text-small font-semibold',
        STATUS_CLASSES[status],
      ].join(' ')}
    >
      {formatSalesStatusLabel(status)}
    </span>
  );
}

export function OrganizationSalesTable({ organizationName, entries }: OrganizationSalesTableProps) {
  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full divide-y divide-neutral-200"
        aria-label={`${organizationName} sales`}
      >
        <thead className="bg-neutral-50">
          <tr className="text-left text-small font-semibold text-neutral-600">
            <th scope="col" className="px-5 py-3">
              Invoice or reference ID
            </th>
            <th scope="col" className="px-5 py-3">
              Date
            </th>
            <th scope="col" className="px-5 py-3">
              Payment method
            </th>
            <th scope="col" className="px-5 py-3">
              Amount
            </th>
            <th scope="col" className="px-5 py-3">
              Status
            </th>
            <th scope="col" className="px-5 py-3">
              Receipt
            </th>
            <th scope="col" className="px-5 py-3">
              Tags
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 bg-white">
          {entries.map((entry) => (
            <tr key={entry.id} className="align-top">
              <td className="px-5 py-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-body font-medium text-neutral-900">
                      {entry.invoiceNumber}
                    </span>
                    <CopyIdButton value={entry.id} label="Copy sales entry ID" />
                  </div>
                  {entry.relatedInvoiceNumber ? (
                    <span className="text-small text-neutral-500">
                      For {entry.relatedInvoiceNumber}
                    </span>
                  ) : null}
                  {entry.isAutoRefill ? (
                    <span className="text-small font-medium text-backoffice-700">Auto-refill</span>
                  ) : null}
                </div>
              </td>
              <td className="whitespace-nowrap px-5 py-4 text-body text-neutral-900">
                {formatDateTime(entry.date)}
              </td>
              <td className="whitespace-nowrap px-5 py-4 text-body text-neutral-900">
                {formatCardLabel(entry.paymentMethod)}
              </td>
              <td
                className={[
                  'whitespace-nowrap px-5 py-4 text-body font-semibold tabular-nums',
                  entry.amount < 0 ? 'text-blue-800' : 'text-neutral-900',
                ].join(' ')}
              >
                {formatMinorAmount(entry.amount, entry.currency)}
              </td>
              <td className="whitespace-nowrap px-5 py-4">
                <StatusBadge status={entry.status} />
              </td>
              <td className="whitespace-nowrap px-5 py-4 text-body text-neutral-900">
                {entry.downloadUrl ? (
                  <a
                    href={entry.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-backoffice-700 hover:text-backoffice-800 hover:underline"
                  >
                    Download
                  </a>
                ) : (
                  'Not available'
                )}
              </td>
              <td className="px-5 py-4">
                <BackofficeTagChips
                  tags={[...(entry.purchaseTags ?? []), ...(entry.paymentTransactionTags ?? [])]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
